import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * On a 401 from our API, renew the token once and replay the request.
 *
 * It deliberately does NOT start a login when the renew fails. Calling authorize() here turns any
 * PERSISTENT 401 into an infinite loop: the redirect goes to AuthServer, which still holds a valid
 * session cookie, so it immediately bounces back to /callback, the app re-issues the same request,
 * gets 401 again, and redirects again — the classic people → callback → people → callback spin.
 *
 * A 401 that survives a token refresh means the token is fine but the API is rejecting it (wrong
 * audience, missing claim, endpoint not deployed). That is a real error and must be shown as one.
 * Starting a login is the guard's job, and only when the user genuinely has no session.
 *
 * The library's own interceptor attaches the bearer (authConfig.secureRoutes); this one only handles
 * expiry mid-flight.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith(environment.apiBaseUrl)) {
    return next(req);
  }

  const oidc = inject(OidcSecurityService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }

      // Never try to renew a renew — a 401 on the retry must propagate, not recurse.
      if (req.headers.has('X-Retried-After-Renew')) {
        return throwError(() => error);
      }

      return oidc.forceRefreshSession().pipe(
        switchMap((result) => {
          const token = result?.accessToken;
          if (!token) {
            return throwError(() => error);
          }

          return next(
            req.clone({
              setHeaders: {
                Authorization: `Bearer ${token}`,
                'X-Retried-After-Renew': '1',
              },
            }),
          );
        }),
        catchError(() => throwError(() => error)),
      );
    }),
  );
};
